/**
 * Shared formatting utilities for PerformanceTab components
 */

// Performance threshold constants
export const SUCCESS_CRITICAL_THRESHOLD = 10; // Below 10% is critical (red)
export const SUCCESS_WARNING_THRESHOLD = 50;  // Below 50% is warning (yellow)
export const RSSI_LOW_THRESHOLD = 3;          // Below 3 is low (red)
export const RSSI_MEDIUM_THRESHOLD = 10;      // Below 10 is medium (yellow)
export const RSSI_NO_SIGNAL_VALUE = 99;       // 99 indicates no signal

// Format percentage values
export const formatPercentage = (value, decimalPlaces = 1) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const numValue = parseFloat(value);
  return `${numValue.toFixed(decimalPlaces)}%`;
};

// Format RSSI values without units
export const formatRSSI = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const numValue = parseFloat(value);
  return numValue.toFixed(1);
};

// Format battery voltage with proper precision
export const formatBatteryVoltage = (value) => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const numValue = parseFloat(value);
  return `${numValue.toFixed(3)} V`;
};

// Format temperature with unit conversion
export const formatTemperature = (value, unit = 'C') => {
  if (value === null || value === undefined || isNaN(value)) return 'N/A';
  const numValue = parseFloat(value);
  
  if (unit === 'F') {
    const fahrenheit = (numValue * 9/5) + 32;
    return `${fahrenheit.toFixed(1)}°F`;
  }
  
  return `${numValue.toFixed(1)}°C`;
};

// Convert temperature between units
export const convertTemperature = (value, fromUnit, toUnit) => {
  if (value === null || value === undefined || isNaN(value)) return value;
  const numValue = parseFloat(value);
  
  if (fromUnit === toUnit) return numValue;
  
  if (fromUnit === 'C' && toUnit === 'F') {
    return (numValue * 9/5) + 32;
  }
  
  if (fromUnit === 'F' && toUnit === 'C') {
    return (numValue - 32) * 5/9;
  }
  
  return numValue;
};

// Format device count with pluralization
export const formatDeviceCount = (count) => {
  if (count === null || count === undefined || isNaN(count)) return 'N/A devices';
  const numValue = parseInt(count, 10);
  return `${numValue} device${numValue !== 1 ? 's' : ''}`;
};

// Format success rate with color coding
export const getSuccessRateColor = (rate, theme) => {
  if (rate === null || rate === undefined || isNaN(rate)) return theme.palette.grey[500];
  
  const numRate = parseFloat(rate);
  if (numRate >= 95) return theme.palette.waltero.fleetStatus.excellent;
  if (numRate >= 90) return theme.palette.waltero.fleetStatus.good;
  if (numRate >= 80) return theme.palette.waltero.fleetStatus.warning;
  return theme.palette.waltero.fleetStatus.critical;
};

// Format RSSI with color coding
export const getRSSIColor = (rssi, theme) => {
  if (rssi === null || rssi === undefined || isNaN(rssi)) return theme.palette.grey[500];
  
  const numRSSI = parseFloat(rssi);
  if (numRSSI >= -70) return theme.palette.waltero.fleetStatus.excellent;
  if (numRSSI >= -85) return theme.palette.waltero.fleetStatus.good;
  if (numRSSI >= -100) return theme.palette.waltero.fleetStatus.warning;
  return theme.palette.waltero.fleetStatus.critical;
};

// Format battery voltage with color coding
export const getBatteryColor = (voltage, theme) => {
  if (voltage === null || voltage === undefined || isNaN(voltage)) return theme.palette.grey[500];
  
  const numVoltage = parseFloat(voltage);
  if (numVoltage >= 3.6) return theme.palette.waltero.fleetStatus.excellent;
  if (numVoltage >= 3.4) return theme.palette.waltero.fleetStatus.good;
  if (numVoltage >= 3.2) return theme.palette.waltero.fleetStatus.warning;
  return theme.palette.waltero.fleetStatus.critical;
};

// Format temperature with color coding based on range
export const getTemperatureColor = (minTemp, maxTemp, globalMin, globalMax) => {
  if (minTemp === null && maxTemp === null) {
    return 'transparent';
  }
  
  // Use the average temperature for color calculation
  const avgTemp = (minTemp + maxTemp) / 2;
  const range = globalMax - globalMin;
  
  if (range === 0) return '#E3F2FD'; // Light blue for no variation
  
  const normalized = (avgTemp - globalMin) / range;
  
  // Create a gradient from blue (cold) to red (hot)
  if (normalized < 0.2) return '#1565C0'; // Dark blue
  if (normalized < 0.4) return '#1976D2'; // Blue
  if (normalized < 0.6) return '#42A5F5'; // Light blue
  if (normalized < 0.8) return '#FF9800'; // Orange
  return '#F44336'; // Red
};

// Format date and time for display
export const formatDateTime = (dateTime) => {
  if (!dateTime) return 'N/A';
  return new Date(dateTime).toLocaleString();
};

// Format date only
export const formatDate = (date) => {
  if (!date) return 'N/A';
  return new Date(date).toLocaleDateString();
};

// Format uptime duration
export const formatUptime = (milliseconds) => {
  if (!milliseconds || milliseconds < 0) return 'N/A';
  
  const seconds = Math.floor(milliseconds / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
  return `${seconds}s`;
};

// Format transmission count
export const formatTransmissionCount = (count) => {
  if (count === null || count === undefined || isNaN(count)) return 'N/A';
  const numValue = parseInt(count, 10);
  
  if (numValue >= 1000) {
    return `${(numValue / 1000).toFixed(1)}K`;
  }
  
  return numValue.toString();
};

// Format payload size
export const formatPayloadSize = (bytes) => {
  if (bytes === null || bytes === undefined || isNaN(bytes)) return 'N/A';
  const numBytes = parseInt(bytes, 10);
  
  if (numBytes >= 1024) {
    return `${(numBytes / 1024).toFixed(1)} KB`;
  }
  
  return `${numBytes} B`;
};

// Validate coordinate values for mapping
export const isValidCoordinate = (lat, lon) => {
  if (lat == null || lon == null) return false;
  if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return false;
  return true;
};

// Format coordinate display
export const formatCoordinate = (lat, lon) => {
  if (!isValidCoordinate(lat, lon)) return 'N/A';
  return `${parseFloat(lat).toFixed(6)}, ${parseFloat(lon).toFixed(6)}`;
};

// Format connection status
export const formatConnectionStatus = (status) => {
  if (!status) return 'Unknown';
  
  const statusMap = {
    'connected': 'Connected',
    'disconnected': 'Disconnected',
    'connecting': 'Connecting',
    'failed': 'Failed',
    'timeout': 'Timeout'
  };
  
  return statusMap[status.toLowerCase()] || status;
};

// Temperature conversion functions
export const celsiusToFahrenheit = (celsius) => {
  if (celsius === null || celsius === undefined || isNaN(celsius)) return null;
  return (parseFloat(celsius) * 9/5) + 32;
};

export const fahrenheitToCelsius = (fahrenheit) => {
  if (fahrenheit === null || fahrenheit === undefined || isNaN(fahrenheit)) return null;
  return (parseFloat(fahrenheit) - 32) * 5/9;
};

export const convertTemperatureValue = (tempInCelsius, unit) => {
  if (tempInCelsius === null || tempInCelsius === undefined) return null;
  return unit === 'fahrenheit' ? celsiusToFahrenheit(tempInCelsius) : tempInCelsius;
};

export const getTemperatureUnitSymbol = (unit) => {
  return unit === 'fahrenheit' ? '°F' : '°C';
};

// Status color classification functions
export const getSuccessColorClass = (percentage) => {
  if (percentage === null || percentage === undefined || isNaN(percentage)) return 'no-data';
  const numPercentage = parseFloat(percentage);
  if (numPercentage < SUCCESS_CRITICAL_THRESHOLD) return 'critical';
  if (numPercentage < SUCCESS_WARNING_THRESHOLD) return 'warning';
  return 'success';
};

export const getRSSIColorClass = (rssi) => {
  if (rssi === RSSI_NO_SIGNAL_VALUE || rssi === null || rssi === undefined || isNaN(rssi)) return 'no-data';
  const numRSSI = parseFloat(rssi);
  if (numRSSI < RSSI_LOW_THRESHOLD) return 'critical';
  if (numRSSI < RSSI_MEDIUM_THRESHOLD) return 'warning';
  return 'success';
};

export const getBatteryColorClass = (voltage) => {
  if (voltage === null || voltage === undefined || isNaN(voltage)) return 'no-data';
  const numVoltage = parseFloat(voltage);
  if (numVoltage >= 3.6) return 'success';
  if (numVoltage >= 3.4) return 'good';
  if (numVoltage >= 3.2) return 'warning';
  return 'critical';
};

// Map marker color functions
export const getRSSIMarkerColor = (rssi, theme) => {
  if (rssi === RSSI_NO_SIGNAL_VALUE || rssi === null || rssi === undefined) return theme.palette.waltero.fleetStatus.noData;
  if (rssi < RSSI_LOW_THRESHOLD) return theme.palette.waltero.fleetStatus.critical;
  if (rssi < RSSI_MEDIUM_THRESHOLD) return theme.palette.waltero.fleetStatus.warning;
  return theme.palette.waltero.fleetStatus.success;
};

export const getSuccessRateMarkerColor = (successRate, theme) => {
  if (successRate === null || successRate === undefined) return theme.palette.waltero.fleetStatus.noData;
  if (successRate < SUCCESS_CRITICAL_THRESHOLD) return theme.palette.waltero.fleetStatus.critical;
  if (successRate < SUCCESS_WARNING_THRESHOLD) return theme.palette.waltero.fleetStatus.warning;
  return theme.palette.waltero.fleetStatus.success;
};

// Sort icon helper for table columns
export const getSortIcon = (sortColumn, currentColumn, sortDirection) => {
  if (sortColumn !== currentColumn) return '↕️'; // Both arrows when not active
  return sortDirection === 'asc' ? '🔼' : '🔽'; // Up or down arrow when active
};

// Heatmap color calculation for connectivity visualization
export const getHeatmapColor = (value, min, max, temperatureUnit = 'C') => {
  if (value === null || value === undefined) return 'rgba(128, 128, 128, 0.1)'; // Light gray for no data
  
  if (min === max) return '#3d8dae'; // Single color if no range
  
  // Normalize value between 0 and 1
  const normalized = (value - min) / (max - min);
  
  // Create a gradient from light blue to dark blue
  const red = Math.floor(255 - (normalized * 155)); // 255 -> 100
  const green = Math.floor(255 - (normalized * 105)); // 255 -> 150
  const blue = Math.floor(255 - (normalized * 81)); // 255 -> 174
  
  return `rgba(${red}, ${green}, ${blue}, 0.8)`;
};